import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { debounce, get } from 'lodash'
import { FormattedMessage } from 'react-intl'
import moment from 'moment'
import { compose } from 'recompose'

import { Title, Button } from 'components/misc'
import { Checkbox } from 'components/fields'
import { Address, PersonalDetails } from 'components/forms'
import { participants } from 'constants/wizards'
import { wizard, meta } from 'helpers/decorators'
import {
  apiCodeLists,
  apiConfiguration,
  apiCommon,
  apiParticipants
} from 'helpers/decorators/api'
import validate from './details.validation'
import { CONFIGURATION_KEYS } from 'constants/enums'
import getParticipantAge from 'helpers/getParticipantAge'
import { connect } from 'react-redux'
import { initializeFormFields } from '../../../../helpers/formInitializationHelper'

const enhance = compose(
  apiConfiguration(),
  apiCodeLists(['genders', 'titles', 'employmentStatuses', 'nationalities'], {
    public: true
  }),
  apiCommon({
    publicCountries: true
  }),
  apiParticipants(),
  wizard({
    form: 'participantRegistration',
    multipage: true,
    destroyOnUnmount: false,
    validate
  }),
  meta({
    title: 'Register participant',
    intlKey: 'pageTitle.register_participant'
  }),
  connect(
    ({ api }) => ({
      registrationInfo: get(api, 'participants.registrationInfo')
    }), {}
  )
)

class Details extends Component {
  static propTypes = {
    links: PropTypes.any,
    codeLists: PropTypes.object,
    configuration: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    changeWizardActive: PropTypes.func.isRequired,
    changeWizardNext: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    onGetConfigurationValue: PropTypes.func.isRequired,
    errors: PropTypes.object,
    common: PropTypes.object,
    values: PropTypes.object,
    isConfigurationTrue: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    registrationInfo: PropTypes.object
  }
  static defaultProps = {}

  constructor(props) {
    super(props)
    this.handleWizardUpdate = debounce(this.handleWizardUpdate, 1000)
    this.state = {}
  }

  componentDidMount = async() => {
    const registrationInfo = get(this.props, 'registrationInfo')

    if (registrationInfo) {
      const postalFieldsFromRegistrationInfo = [
        'postalLine1', 'postalLine2', 'postalCity',
        'postalZipCode', 'postalStateOrProvinceOrRegion', 'postalCountryId'
      ]
      const postalAddressChecked = postalFieldsFromRegistrationInfo.some(field => get(registrationInfo, field, null) !== null)
      const fieldsToInit = [
        // PersonDetails
        { name: 'title', value: get(registrationInfo, 'title', null) },
        { name: 'firstName', value: get(registrationInfo, 'firstName', null) },
        { name: 'middleName', value: get(registrationInfo, 'middleName', null) },
        { name: 'lastName', value: get(registrationInfo, 'lastName', null) },
        { name: 'minAgeConfirmation', value: get(registrationInfo, 'minAgeConfirmation', null) },
        { name: 'birthDate', value: get(registrationInfo, 'birthDate', null) },
        { name: 'gender.value', value: get(registrationInfo, 'gender.value', null) },
        { name: 'nationality.value', value: get(registrationInfo, 'nationality.value', null) },
        { name: 'employmentStatus.value', value: get(registrationInfo, 'employmentStatus.value', null) },
        // Address
        { name: 'email', value: get(registrationInfo, 'email', null) },
        { name: 'phoneNumber', value: get(registrationInfo, 'phoneNumber', null) },
        { name: 'line1', value: get(registrationInfo, 'line1', null) },
        { name: 'line2', value: get(registrationInfo, 'line2', null) },
        { name: 'city', value: get(registrationInfo, 'city', null) },
        { name: 'zipCode', value: get(registrationInfo, 'zipCode', null) },
        { name: 'stateOrProvinceOrRegion', value: get(registrationInfo, 'stateOrProvinceOrRegion', null) },
        { name: 'addressCountryId', value: get(registrationInfo, 'addressCountryId', null) },
        // Address (postal)
        { name: 'postal.line1', value: get(registrationInfo, 'postalLine1', null) },
        { name: 'postal.line2', value: get(registrationInfo, 'postalLine2', null) },
        { name: 'postal.city', value: get(registrationInfo, 'postalCity', null) },
        { name: 'postal.zipCode', value: get(registrationInfo, 'postalZipCode', null) },
        { name: 'postal.stateOrProvinceOrRegion', value: get(registrationInfo, 'postalStateOrProvinceOrRegion', null) },
        { name: 'postal.addressCountryId', value: get(registrationInfo, 'postalCountryId', null) },
        // Helpers
        { name: 'helpers.postalAddressChecked', value: postalAddressChecked },
      ]

      initializeFormFields(this.props, fieldsToInit)
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.values && get(prevProps, 'values.birthDate')) {
      this.handleWizardUpdate(null, this.props.values.birthDate)
      return
    }

    if (
      get(this.props, 'values.birthDate') !== get(prevProps, 'values.birthDate')
    ) {
      this.handleWizardUpdate(
        get(prevProps, 'values.birthDate'),
        get(this.props, 'values.birthDate')
      )
    }
  }

  handleWizardUpdate = (oldValue, newValue) => {
    const { steps, rootPath } = participants
    const { links } = this.props
    const profile = links.findEntry(item => item.id === steps.profile.id)
    const payment = links.findEntry(item => item.id === steps.payment.id)

    if (oldValue !== newValue) {
      if (
        moment().diff(newValue, 'years') <
        getParticipantAge(this.props.configuration)
      ) {
        this.props.changeWizardActive(steps.parent.id, true)
        this.props.changeWizardNext(
          steps.details.id,
          rootPath + steps.parent.path
        )
        this.props.change('helpers.isUnderAge', true)
      } else {
        this.props.change('helpers.isUnderAge', false)
        this.props.changeWizardActive(steps.parent.id, false)

        let nextActive = steps.account.path

        if (profile[1].active) {
          nextActive = steps.profile.path
        } else if (payment[1].active) {
          nextActive = steps.payment.path
        }

        this.props.changeWizardNext(steps.details.id, rootPath + nextActive)
      }
    }
  }

  render() {
    const {
      handleSubmit,
      codeLists,
      onSubmit,
      change,
      common,
      values
    } = this.props

    const showPostalAddress = this.props.isConfigurationTrue(CONFIGURATION_KEYS.postalAddress.id)

    const showTitle = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationTitle.id)

    const showMiddleName = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationMiddleName.id)

    const showNationality = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationNationality.id)

    const showEmplStatus = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationEmplStatus.id)

    const showAddressLine1 = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressLine1.id)

    const showAddressLine2 = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressLine2.id)

    const showCity = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressCity.id)

    const showZipCode = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressZipCode.id)

    const showRegion = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressRegion.id)

    const showCountry = this.props.isConfigurationTrue(CONFIGURATION_KEYS.participantRegistrationAddressCountry.id)

    const postalAddressChecked = get(values, 'helpers.postalAddressChecked')

    const showHomeAddress = showAddressLine1 || showAddressLine2 || showCity || showZipCode || showRegion || showCountry

    return (
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-testId="registrationDetailsForm"
      >
        <Title h1>
          <FormattedMessage
            id="moduleTitle.personal_details"
            defaultMessage="Personal Details"
          />
        </Title>

        <PersonalDetails
          titles={codeLists.titles.list}
          nationalities={showNationality ? codeLists.nationalities.list : undefined}
          employmentStatuses={showEmplStatus ? codeLists.employmentStatuses.list : undefined}
          genders={codeLists.genders.list}
          change={change}
          errors={this.props.errors}
          showTitle={showTitle}
          showMiddleName={showMiddleName}
        />

        {showHomeAddress && <div>
          <Title h2>
            <FormattedMessage
              id="moduleTitle.home_address"
              defaultMessage="Home Address"
            />
          </Title>
          <Address countries={showCountry ? common.countries.list : undefined}
                   showFirstLine={showAddressLine1}
                   showSecondLine={showAddressLine2}
                   showCity={showCity}
                   showZipCode={showZipCode}
                   showRegion={showRegion}
          />
        </div>}

        {showPostalAddress && (
          <Checkbox
            name="helpers.postalAddressChecked"
            onChange={this.handleCheckPostalAddressChange}
            intlKey="label.postal_address_different"
            label="My postal address is different"
          />
        )}

        {postalAddressChecked && (
          <div>
            <Title h2>
              <FormattedMessage
                id="moduleTitle.postal_address"
                defaultMessage="Postal Address"
              />
            </Title>
            <Address prefix="postal"
                     countries={common.countries.list}
                     showFirstLine
                     showSecondLine
                     showCity
                     showZipCode
                     showRegion
                     showCountry
            />
          </div>
        )}

        <Button>
          <FormattedMessage id="button.continue" defaultMessage="Continue" />
        </Button>
      </form>
    )
  }
}

export default enhance(Details)
